// NOTE: This file is imported transitively by client components via
// `lib/gating.ts` and `components/documents/suggestions-panel.tsx`.
// It MUST stay free of Prisma / DB imports or Next.js will try to bundle
// `pg` (and its Node-only deps `tls` / `net` / `fs`) into the browser
// chunk and fail at build time. The DB-touching `getUserPlanInfo()` helper
// lives in `lib/plan-server.ts` — import it from there in server code.

/**
 * Plan tier hierarchy (each tier is a SUPERSET of the one below it):
 *
 *   site_control  ⊇  business  ⊇  pro (pro_monthly ≡ pro_annual)  ⊇  starter
 *
 * - `starter` — entry tier, 1-project limit, no paid features.
 * - `pro_*` — unlocks every PRO_FEATURE (lib/gating.ts). pro_monthly and
 *   pro_annual are entitlement-identical; they differ only in billing cadence.
 * - `business` — Pro + the BUSINESS_FEATURES (multi-user / Team, site portal).
 * - `site_control` — the premium per-site enterprise tier ABOVE business
 *   (£675/mo). It INHERITS the ENTIRE Business + Pro product — Team, the whole
 *   Site Access surface (portal users, site files, RFIs, attendance, inspections,
 *   document review), every Pro feature — and is the paid tier site_control's
 *   marketing is built around. It is NOT a parallel/lesser product.
 *
 * GATING RULE (so the hierarchy holds): gate features through
 * `canAccessFeature` (lib/gating.ts) or the tier predicates below — NEVER with an
 * exactly-`"business"` comparison, or you exclude site_control from a feature it
 * pays for. `isProPlus` / `isBusinessOrHigher` already include site_control;
 * `isBusiness` is "exactly business" and is for BILLING / DISPLAY only.
 * (Product decision confirmed 2026-06-09. The bug this prevents — ~50 exactly-
 * business gates excluding site_control — is in
 * tickets/SITE-CONTROL-ENTITLEMENT-GATES-2026-06-08.md.)
 */
import { type PlanTier } from "@thesitebook/contracts";

export type Plan =
  | "starter"
  | "pro_monthly"
  | "pro_annual"
  | "business"
  | "site_control";

export const STARTER_PROJECT_LIMIT = 1;
// Starter tier: unlimited documents on your first project (was 3)
// This lets users experience the full workflow before upgrading
export const STARTER_DOC_LIMIT = Infinity;
export const STARTER_PROJECT_DELETE_LOCKED_ERROR = "starter_project_delete_locked";
export const STARTER_PROJECT_DELETE_LOCKED_MESSAGE =
  "Starter includes one project. Upgrade to Pro if you want to start another project.";

// Legacy aliases — kept for one migration cycle so call sites that still
// reference the pre-Phase-42 names keep compiling. Remove after Wave 3
// clean-up pass.
export const FREE_PROJECT_LIMIT = STARTER_PROJECT_LIMIT;
export const FREE_DOC_LIMIT = STARTER_DOC_LIMIT;

export function isProPlus(plan: Plan): boolean {
  return (
    plan === "pro_monthly" ||
    plan === "pro_annual" ||
    plan === "business" ||
    plan === "site_control"
  );
}

export function isBusiness(plan: Plan): boolean {
  return plan === "business";
}

/** Site Control — the per-site enterprise tier (sits above Business). */
export function isSiteControl(plan: Plan): boolean {
  return plan === "site_control";
}

/**
 * Which plans are OFFERED the £249 setup-service call-back.
 *
 * The tier map itself lives in `@thesitebook/contracts`
 * (`entities/setup-callback.ts`) and is re-exported here so every existing web
 * call site keeps its `@/lib/plan` import. It moved there for #2269: the
 * NATIVE Home card has to make the same render decision the web dashboard
 * makes, and React Native cannot import `lib/`. One map, read by the dashboard
 * mount, the native card, `POST /api/leads/setup-callback` and
 * `POST /api/mobile/v1/leads/setup-callback` — so no surface can disagree with
 * the route the way they did in #2061.
 *
 * Same shape as `lib/geocode-sentinels.ts` re-exporting `isRealNearestHospital`.
 */
export { isSetupServiceOfferable } from "@thesitebook/contracts";

// The offer map's `satisfies Record<PlanTier, boolean>` only forces a decision
// for a NEW tier while the contracts-side `PlanTier` union and this file's
// `Plan` union are the same set. Assert that here, in both directions, so
// adding a tier on either side is a compile error rather than a silent
// `?? false` (i.e. a paid-service prompt quietly withheld from a paying tier).
type _PlanTierDrift = Exclude<Plan, PlanTier> | Exclude<PlanTier, Plan>;
const _planUnionsMatch: _PlanTierDrift extends never ? true : never = true;
void _planUnionsMatch;

/**
 * True for Business AND Site Control. Use this to gate "Business" features
 * (multi-user, site portal) so the higher Site Control tier inherits them.
 * `isBusiness` stays "exactly Business" for billing / display call sites.
 */
export function isBusinessOrHigher(plan: Plan): boolean {
  return plan === "business" || plan === "site_control";
}

/**
 * The dunning ladder's payment-failure window (app/api/cron/payment-recovery):
 * a still-failing PRO account is auto-downgraded to Starter this many days
 * after `paymentFailedAt`. Customer-facing countdown copy must derive from
 * this constant so the deadline shown can never drift from the one enforced.
 */
export const PAYMENT_FAILED_DOWNGRADE_DAYS = 10;

/**
 * Exactly the tiers the payment-recovery cron AUTO-DOWNGRADES after
 * PAYMENT_FAILED_DOWNGRADE_DAYS. Business and Site Control are deliberately
 * excluded from that 10-day ladder (commercial posture: high-value accounts
 * get a human recovery conversation — Site Control additionally fires a
 * day-10 ops alert). Scope note: this covers ONLY the cron's ladder; Stripe
 * terminal cancellation (subscription.deleted / terminal updated) still
 * downgrades every tier via the webhook. An exact-tier check like
 * `isBusiness` — billing-lifecycle only, never an entitlement gate. Show a
 * switch-off countdown ONLY to plans this returns true for; other paid tiers
 * must not see a fabricated deadline.
 */
export function isAutoDowngradeTier(plan: Plan): boolean {
  return plan === "pro_monthly" || plan === "pro_annual";
}

/**
 * Human-readable plan name for internal alerts and billing/display surfaces
 * (Slack upgrade + churn alerts share this so the same customer reads the
 * same across channels). Display only — never use for entitlement gating.
 */
export function planLabel(plan: Plan): string {
  switch (plan) {
    case "site_control":
      return "Site Control";
    case "business":
      return "Business";
    case "pro_annual":
      return "Pro Annual";
    case "pro_monthly":
      return "Pro Monthly";
    default:
      return "Starter";
  }
}

/**
 * Numeric tier rank for ordering plans (NOT for entitlement gating — use the
 * predicates above for that). Higher = more premium. `pro_monthly` and
 * `pro_annual` share rank 1 because they are entitlement-identical (they differ
 * only in billing cadence). Used by the billing-event taxonomy (G1-2) to detect
 * upgrade vs downgrade direction on a plan change.
 */
export function planRank(plan: Plan): number {
  switch (plan) {
    case "site_control":
      return 3;
    case "business":
      return 2;
    case "pro_monthly":
    case "pro_annual":
      return 1;
    case "starter":
    default:
      return 0;
  }
}

/**
 * Runtime enumeration of every plan tier, kept exhaustive by the compile-time
 * assertion below: add a member to the `Plan` union and TypeScript forces you to
 * add it here too. The local E2E plan-drift guard
 * (`__tests__/e2e-harness/plan-coverage.test.ts`) reads this so the test-suite
 * personas (`e2e/personas.ts`) can't silently fall behind a new plan tier.
 */
export const PLAN_TIERS = [
  "starter",
  "pro_monthly",
  "pro_annual",
  "business",
  "site_control",
] as const satisfies readonly Plan[];

// Compile-time exhaustiveness. `satisfies` above proves every entry is a real
// Plan; this proves the converse — every Plan member is listed. If a new union
// member is missing from PLAN_TIERS, `_MissingPlanTier` is non-`never` and the
// assignment below fails to compile.
type _MissingPlanTier = Exclude<Plan, (typeof PLAN_TIERS)[number]>;
const _planTiersAreExhaustive: _MissingPlanTier extends never ? true : never = true;
void _planTiersAreExhaustive;

// `getUserPlanInfo()` has moved to `lib/plan-server.ts` — import it from
// `@/lib/plan-server` in server-only code. It is NOT re-exported from here
// because a re-export would still pull Prisma into client bundles.
